import { useMemo, useRef } from 'react';
import { Box, Button, Checkbox, Group, Text, TextInput } from '@mantine/core';
import { useForm, isEmail, hasLength } from '@mantine/form';

export interface AdminUserFormValues {
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  sendInvite: boolean;
  markEmailAsVerified: boolean;
  enableTrial: boolean;
}

interface AdminUserFormProps {
  mode: 'create' | 'edit';
  loading?: boolean;
  initialValues?: Partial<AdminUserFormValues>;
  onCancel: () => void;
  onSubmit: (values: AdminUserFormValues) => Promise<void> | void;
}

function AdminUserForm({ mode, loading = false, initialValues, onCancel, onSubmit }: AdminUserFormProps) {
  const initialEmail = initialValues?.email || '';
  const initialFirstName = initialValues?.firstName || '';
  const initialLastName = initialValues?.lastName || '';
  const initialDisplayName = initialValues?.displayName || '';
  const initialSendInvite = initialValues?.sendInvite ?? true;
  const initialMarkEmailAsVerified = initialValues?.markEmailAsVerified ?? true;
  const initialEnableTrial = initialValues?.enableTrial ?? true;

  const normalizedInitialValues = useMemo(
    () => ({
      email: initialEmail,
      firstName: initialFirstName,
      lastName: initialLastName,
      displayName: initialDisplayName,
      sendInvite: initialSendInvite,
      markEmailAsVerified: initialMarkEmailAsVerified,
      enableTrial: initialEnableTrial,
    }),
    [
      initialEmail,
      initialFirstName,
      initialLastName,
      initialDisplayName,
      initialSendInvite,
      initialMarkEmailAsVerified,
      initialEnableTrial,
    ]
  );

  const form = useForm<AdminUserFormValues>({
    mode: 'uncontrolled',
    validateInputOnChange: true,
    initialValues: normalizedInitialValues,
    validate: {
      email: isEmail('Invalid email'),
      firstName: hasLength({ min: 1, max: 30 }, 'First name must be between 1 and 30 characters'),
      lastName: hasLength({ min: 1, max: 30 }, 'Last name must be between 1 and 30 characters'),
      displayName: hasLength({ min: 1, max: 30 }, 'Display name must be between 1 and 30 characters'),
    },
  });

  const firstNameInputProps = form.getInputProps('firstName');
  const lastNameInputProps = form.getInputProps('lastName');
  const displayNameInputProps = form.getInputProps('displayName');
  const displayNameInteractedRef = useRef(false);

  const syncDisplayNameFromNames = () => {
    if (mode === 'edit') return;
    // If the user has already interacted with the display name field, we won't auto-sync it anymore to avoid overwriting their input
    if (displayNameInteractedRef.current) return;

    const { firstName, lastName } = form.getValues();
    form.setFieldValue('displayName', `${firstName.trim()} ${lastName.trim()}`.trim());
  };

  return (
    <Box maw={500} mx="auto">
      <Text>{mode}</Text>
      <form onSubmit={form.onSubmit(onSubmit)}>
        <TextInput
          label="Email"
          placeholder="user@example.com"
          autoFocus={mode === 'create'}
          data-autofocus={mode === 'create' ? true : undefined}
          required={mode === 'create'}
          disabled={mode === 'edit'}
          {...form.getInputProps('email')}
        />
        {mode === 'create' && (
          <>
            <Checkbox
              mt="md"
              label="Mark email address as verified"
              {...form.getInputProps('markEmailAsVerified', { type: 'checkbox' })}
            />
            <Checkbox mt="md" label="Send invite email" {...form.getInputProps('sendInvite', { type: 'checkbox' })} />
            <Checkbox mt="md" label="Enable trial" {...form.getInputProps('enableTrial', { type: 'checkbox' })} />
          </>
        )}
        <TextInput
          mt="md"
          label="First Name"
          placeholder="First name"
          required
          autoFocus={mode === 'edit'}
          data-autofocus={mode === 'edit' ? true : undefined}
          {...firstNameInputProps}
          onBlur={(event) => {
            firstNameInputProps.onBlur(event);
            syncDisplayNameFromNames();
          }}
        />
        <TextInput
          mt="md"
          label="Last Name"
          placeholder="Last name"
          required
          {...lastNameInputProps}
          onBlur={(event) => {
            lastNameInputProps.onBlur(event);
            syncDisplayNameFromNames();
          }}
        />
        <TextInput
          mt="md"
          label="Display Name"
          placeholder="Display name"
          required
          {...displayNameInputProps}
          onFocus={(event) => {
            displayNameInteractedRef.current = true;
            displayNameInputProps.onFocus?.(event);
          }}
        />

        <Group mt="md" justify="flex-end">
          <Button type="submit" loading={loading}>
            {mode === 'create' ? 'Create User' : 'Save Changes'}
          </Button>
          <Button variant="default" type="button" onClick={onCancel}>
            Cancel
          </Button>
        </Group>
      </form>
    </Box>
  );
}

export default AdminUserForm;
